import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { keyframes } from "@mui/material/styles";
import ProjectCard from "./ProjectCards";
import projectData from "../../projects_data";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

function Projects() {
  const featuredProjects = projectData.filter((project) => project.featured);
  const otherProjects = projectData.filter((project) => !project.featured);

  return (
    <Box
      component="section"
      id="projects"
      sx={{
        py: { xs: 8, md: 12 },
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Background accent */}
      <Box
        sx={{
          position: "absolute",
          top: "20%",
          right: "-10%",
          width: "30%",
          height: "30%",
          background: "radial-gradient(circle, rgba(0, 127, 255, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
            animation: `${fadeInUp} 0.6s ease-out`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              letterSpacing: 3,
              mb: 1,
              display: "block",
            }}
          >
            Portfolio
          </Typography>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(135deg, #ffffff 0%, #007FFF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Case Studies & Project Portfolio
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Selected work across enterprise SaaS, Oracle Fusion API integration,
            invoicing and accounting systems, React/Next.js dashboards, and Web3 DApps.
          </Typography>
        </Box>

        {/* Featured Projects */}
        {featuredProjects.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 4,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&::before": {
                  content: '""',
                  width: 24,
                  height: 3,
                  backgroundColor: "primary.main",
                  borderRadius: 1,
                },
              }}
            >
              Featured Work
            </Typography>
            <Grid container spacing={4}>
              {featuredProjects.map((project, index) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={project.id}
                  sx={{
                    animation: `${fadeInUp} 0.6s ease-out`,
                    animationDelay: `${index * 0.1}s`,
                    animationFillMode: "both",
                  }}
                >
                  <ProjectCard project={project} featured />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Other Projects */}
        <Box>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 4,
              display: "flex",
              alignItems: "center",
              gap: 1,
              "&::before": {
                content: '""',
                width: 24,
                height: 3,
                backgroundColor: "secondary.main",
                borderRadius: 1,
              },
            }}
          >
            More Projects
          </Typography>
          <Grid container spacing={3}>
            {otherProjects.map((project, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={project.id}
                sx={{
                  animation: `${fadeInUp} 0.6s ease-out`,
                  animationDelay: `${(index + featuredProjects.length) * 0.1}s`,
                  animationFillMode: "both",
                }}
              >
                <ProjectCard project={project} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default Projects;
